/**
 * ONE rule for reading and displaying a currency code, because case is part of its identity.
 *
 * FOUNDER, 2026-09-12: "database is case sensitive. you need to be case sensitive too on the exchange
 * rate", "we need exact cases for all FX abbreviations", and — after I proposed leaving the display
 * formatters alone — "We cannot use Display-only .toUpperCase() for currency symbols since we need to
 * be exact."
 *
 * WHY IT MATTERS AT THE LAST MILE TOO. The rate feed quotes MINOR units beside the ISO ones: `GBP` =
 * 0.739098 pounds per USD and `GBp` = 73.9098 PENCE per USD, plus `USc`, `ZAc`, `ILa`. Upper-casing a
 * code in the rate lookup picked the wrong unit and put "£7.46B" on a member's plan. Upper-casing it in
 * a FORMATTER is the same lie one layer later: an amount held in pence, rendered "£813,007,800", reads
 * as eight hundred million pounds. The number is right and the label makes it wrong.
 *
 * THE INTL TRAP. `Intl.NumberFormat` canonicalises currency codes case-insensitively, so
 * `{ currency: 'GBp' }` does NOT throw — it renders a pound sign. A formatter cannot lean on Intl to
 * reject a minor unit; the rule has to be stated here.
 *
 * THE RULE, naming no currency: ISO 4217 codes are upper-case by definition, so only an all-upper-case
 * code may be shown as a SYMBOL. Anything else is a minor-unit or vendor code and is shown as its exact
 * text beside the amount ("813,007,800 GBp"), which is unambiguous and cannot be misread as pounds.
 */

/** A currency code exactly as given, or null when the input is not a code at all. Never case-folded. */
export function exactCurrencyCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const code = raw.trim()
  return /^[A-Za-z]{3}$/.test(code) ? code : null
}

/**
 * What a member TYPED as a currency answer, ready to send — or null. Never case-folded: `GBp` is
 * pence and `GBP` is pounds (founder, 2026-09-14: "all input currencies by user need to be checked
 * to make sure they are right"). The server validates the code against the rate table exactly as
 * spelled; this only refuses to send something that is not a code at all.
 */
export function submittableCurrencyCode(raw: unknown): string | null {
  return exactCurrencyCode(raw)
}

/** True when this code is the ISO 4217 (major) unit rather than a minor-unit quote like `GBp`. */
export function isMajorUnitCode(code: string): boolean {
  return code === code.toUpperCase()
}

/**
 * A DOLLAR OTHER THAN THE US DOLLAR, AND THE YUAN, NEVER PRINT AS A BARE "$" OR "¥" (founder-approved,
 * 2026-09-21, in every language, English included). A reader takes "$" for US dollars and "¥" for yen,
 * so a Canadian plan reading "$250,000" is misread before it is read. The workbook and the instant check
 * already wrote these; this is the one table every money formatter now shares.
 */
export const DISTINCT_CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'S$', MXN: 'MX$', TWD: 'NT$', CNY: 'CN¥',
}

// The currency each shared glyph belongs to. Any other currency Intl would write with it (the Chilean or
// Argentine peso's "$", the Falklands pound's "£") is written as its ISO code after the amount — ONE
// rendering, the one every reader (page, money parser, letter gate) takes back as itself.
const SHARED_SYMBOL_OWNER: Readonly<Record<string, string>> = { '$': 'USD', '¥': 'JPY', '£': 'GBP' }

function intlSymbol(code: string): string {
  try {
    const c = new Intl.NumberFormat('en-US', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0).find((p) => p.type === 'currency')?.value
    // Intl returns the ISO code itself when there is no distinct symbol (e.g. 'GHS') -> no symbol.
    return c && c !== code ? c : ''
  } catch {
    return ''
  }
}

/**
 * How to render money in `code`: a symbol prefix when the code is a real ISO 4217 code, otherwise the
 * exact code as a suffix. `isoSuffix` already carries its leading space.
 */
const _symbolCache = new Map<string, string>()
export function currencyAffixes(rawCode: string): { prefix: string; isoSuffix: string } {
  const code = exactCurrencyCode(rawCode) || 'USD'
  // A minor-unit code never gets a symbol: `Intl` would hand back the MAJOR unit's sign.
  if (!isMajorUnitCode(code)) return { prefix: '', isoSuffix: ` ${code}` }
  if (!_symbolCache.has(code)) {
    let sym = DISTINCT_CURRENCY_SYMBOLS[code] ?? intlSymbol(code)
    const owner = SHARED_SYMBOL_OWNER[sym]
    if (owner && owner !== code) sym = ''
    _symbolCache.set(code, sym)
  }
  const prefix = _symbolCache.get(code) || ''
  return { prefix, isoSuffix: prefix ? '' : ` ${code}` }
}
